import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, doc, query, getDocs, Timestamp, getDoc, updateDoc, deleteDoc, where, writeBatch } from 'firebase/firestore';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { X, Clock, User, ChevronLeft, ChevronRight, AlertCircle, Shield, Check, Calendar, Trash2, RotateCcw } from 'lucide-react';
import EnhancedAppointmentDetailsModal from './EnhancedAppointmentDetailsModal';
import { useTheme } from '../contexts/ThemeContext';
import EnhancedModal, { ModalHeader, ModalBody, ModalFooter, ModalButton } from './EnhancedModal';
import EnhancedEditAppointmentModal from './EnhancedEditAppointmentModal';

const DayViewMobile = ({ db, currentDate, currentUser, onDateChange, showCompletedAppointments }) => {
  const { theme } = useTheme();

  // Vérifier si currentUser est null et retourner un message d'attente
  if (!currentUser || !currentUser.userId) {
    return (
      <div className="day-view-mobile flex flex-col h-full overflow-hidden">
        <div className="container mx-auto px-4 py-3">
          <div className="text-center text-gray-500">
            <p>Chargement des informations utilisateur...</p>
          </div>
        </div>
      </div>
    );
  }

  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedEditAppointment, setSelectedEditAppointment] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDetailAppointment, setSelectedDetailAppointment] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedUnassignedAppointment, setSelectedUnassignedAppointment] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedInfoAppointment, setSelectedInfoAppointment] = useState(null);
  const [clickTimer, setClickTimer] = useState(null);
  const clickDelay = 200;
  const [userPermissions, setUserPermissions] = useState({
    read: false,
    write: false,
    create: false,
    delete: false,
    manage: false
  });
  const [showRecurrenceModal, setShowRecurrenceModal] = useState(false);
  const [recurrenceAction, setRecurrenceAction] = useState(null);
  const [pendingRecurrenceData, setPendingRecurrenceData] = useState(null);

  const fetchEmployees = useCallback(async () => {
    if (!db) return;
    try {
      const employeesRef = collection(db, 'employees');
      const q = query(employeesRef, where('isArchived', '==', false));

      // Promesses parallèles : liste filtrée ET utilisateur courant spécifique
      const [querySnapshot, currentUserDoc] = await Promise.all([
        getDocs(q),
        currentUser ? getDoc(doc(db, 'employees', currentUser.userId)) : Promise.resolve(null)
      ]);

      // Map des employés filtrés
      const fetchedEmployeesMap = new Map();
      querySnapshot.docs.forEach(doc => {
        fetchedEmployeesMap.set(doc.id, {
          userId: doc.id,
          ...doc.data(),
          color: doc.data().color || '#808080'
        });
      });

      // AJOUT FORCE DE L'UTILISATEUR COURANT s'il existe et n'est pas déjà présent
      if (currentUserDoc && currentUserDoc.exists()) {
        const userData = currentUserDoc.data();
        fetchedEmployeesMap.set(currentUserDoc.id, {
          userId: currentUserDoc.id,
          ...userData,
          color: userData.color || '#808080'
        });
      }

      setEmployees(Array.from(fetchedEmployeesMap.values()));
    } catch (error) {
      console.error("Erreur lors de la récupération des employés:", error);
    }
  }, [db, currentUser]);

  const fetchAppointments = useCallback(async () => {
    if (!db || !currentUser) return;
    try {
      const startOfDay = new Date(currentDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(currentDate);
      endOfDay.setHours(23, 59, 59, 999);

      let appointmentsQuery = query(
        collection(db, 'appointments'),
        where('date', '>=', Timestamp.fromDate(startOfDay)),
        where('date', '<=', Timestamp.fromDate(endOfDay))
      );

      const querySnapshot = await getDocs(appointmentsQuery);
      const fetchedAppointments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date(),
      }));

      const appointmentsWithClientInfo = await Promise.all(fetchedAppointments.map(async appointment => {
        const clientDoc = await getDoc(doc(db, 'clients', appointment.clientId));
        const clientData = clientDoc.data();
        return {
          ...appointment,
          clientName: clientData ? `${clientData.lastName} ${clientData.firstName}` : 'Bénéficiaire non spécifié',
          clientColor: clientData ? clientData.color : '#808080',
          clientAddress: clientData ? clientData.address : '',
          clientPhone: clientData ? clientData.phoneNumber : '',
          clientEmail: clientData ? clientData.email : '',
          status: appointment.status || 'libre'
        };
      }));

      // DayView mobile affiche les rendez-vous de l'utilisateur connecté ET les rendez-vous libres
      const filteredAppointments = appointmentsWithClientInfo.filter(app =>
        app.employeeId === currentUser.userId ||
        !app.employeeId ||
        app.status === 'libre' ||
        app.status === 'reserve'
      );

      setAppointments(filteredAppointments);
    } catch (error) {
      console.error("Erreur lors de la récupération des rendez-vous:", error);
    }
  }, [db, currentDate, currentUser]);

  useEffect(() => {
    const fetchClients = async () => {
      if (!db) return;
      try {
        const clientsRef = collection(db, 'clients');
        const q = query(clientsRef, where('isActive', '==', true));
        const clientsSnapshot = await getDocs(q);
        const fetchedClients = clientsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setClients(fetchedClients);
      } catch (error) {
        console.error("Erreur lors de la récupération des clients:", error);
      }
    };
    fetchClients();
  }, [db]);

  // Fonction pour récupérer les permissions de l'utilisateur
  const fetchUserPermissions = useCallback(async () => {
    if (!db || !currentUser) return;
    try {
      const userDoc = await getDoc(doc(db, 'employees', currentUser.userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userRoleId = userData.role;

        // Essayer d'abord le nouveau système advanced_roles
        try {
          const advancedRoleDoc = await getDoc(doc(db, 'advanced_roles', userRoleId));
          if (advancedRoleDoc.exists()) {
            const roleData = advancedRoleDoc.data();
            const appointmentsPerms = roleData.permissions?.appointments || {};
            setUserPermissions({
              read: appointmentsPerms.read || false,
              write: appointmentsPerms.write || false,
              create: appointmentsPerms.create || false,
              delete: appointmentsPerms.delete || false,
              manage: appointmentsPerms.manage || false
            });
            return;
          }
        } catch (advancedError) {
          console.log("Nouveau système de permissions non disponible, utilisation du fallback...");
        }

        // Fallback: ancien système
        const roleDoc = await getDoc(doc(db, 'roles', userRoleId));
        if (roleDoc.exists()) {
          const roleData = roleDoc.data();
          setUserPermissions({
            read: roleData.permissions.includes('read_appointments'),
            write: roleData.permissions.includes('write_appointments'),
            create: roleData.permissions.includes('write_appointments'),
            delete: roleData.permissions.includes('write_appointments'),
            manage: roleData.permissions.includes('write_appointments')
          });
        }
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des permissions de l'utilisateur:", error);
    }
  }, [db, currentUser]);

  useEffect(() => {
    fetchUserPermissions();
  }, [fetchUserPermissions]);

  useEffect(() => {
    fetchEmployees();
    fetchAppointments();
  }, [fetchEmployees, fetchAppointments]);

  const filteredAppointments = useMemo(() => {
    return appointments.filter(appointment => {
      // Inclure les rendez-vous terminés si la case est cochée
      const isCompleted = appointment.status === 'completed';
      const shouldShow = showCompletedAppointments || !isCompleted;

      return shouldShow;
    });
  }, [appointments, showCompletedAppointments]);

  const sortedUnassignedAppointments = useMemo(() => {
    return filteredAppointments
      .filter(app => !app.employeeId || app.status === 'libre' || app.status === 'reserve')
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [filteredAppointments]);

  const groupedAssignedAppointments = useMemo(() => {
    const grouped = {};
    employees.forEach(employee => {
      grouped[employee.userId] = filteredAppointments
        .filter(app => app.employeeId === employee.userId)
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
    });
    return grouped;
  }, [filteredAppointments, employees]);

  // DayView mobile n'affiche QUE l'utilisateur connecté
  const visibleEmployees = useMemo(() => {
    return employees.filter(emp => emp.userId === currentUser.userId);
  }, [employees, currentUser.userId]);

  // Fonction pour vérifier si l'utilisateur a une permission spécifique
  const hasPermission = useCallback((action) => userPermissions[action], [userPermissions]);

  const handleAppointmentClick = (appointment, event) => {
    event.preventDefault();
    event.stopPropagation();

    if (clickTimer) {
      // Double-clic détecté
      clearTimeout(clickTimer);
      setClickTimer(null);
      handleAppointmentDoubleClick(appointment);
    } else {
      // Définir un timer pour le simple clic
      const timer = setTimeout(() => {
        handleSingleClick(appointment);
        setClickTimer(null);
      }, clickDelay);
      setClickTimer(timer);
    }
  };

  const handleSingleClick = (appointment) => {
    if (appointment.status === 'libre') {
      setSelectedUnassignedAppointment(appointment);
      setShowBookingModal(true);
    } else if (appointment.status === 'reserve') {
      setSelectedInfoAppointment(appointment);
      setShowInfoModal(true);
    } else {
      setSelectedDetailAppointment(appointment);
      setShowDetailsModal(true);
    }
  };

  const handleAppointmentDoubleClick = (appointment) => {
    // DayView mobile permet l'édition de tous les rendez-vous de l'utilisateur
    setSelectedEditAppointment(appointment);
    setShowEditModal(true);
  };

  const handleUnassignedAppointmentClick = (appointment) => {
    if (appointment.status === 'libre') {
      setSelectedUnassignedAppointment(appointment);
      setShowBookingModal(true);
    } else if (appointment.status === 'reserve') {
      setSelectedInfoAppointment(appointment);
      setShowInfoModal(true);
    }
  };

  const handleBookAppointment = async () => {
    if (!selectedUnassignedAppointment) return;

    try {
      const appointmentRef = doc(db, 'appointments', selectedUnassignedAppointment.id);
      await updateDoc(appointmentRef, {
        employeeId: currentUser.userId,
        status: 'reserve'
      });

      fetchAppointments();
      setShowBookingModal(false);
    } catch (error) {
      console.error("Erreur lors de la réservation du rendez-vous:", error);
    }
  };

  // Fonction pour approuver un rendez-vous en attente
  const handleApproveAppointment = async () => {
    if (!selectedInfoAppointment) return;

    try {
      const appointmentRef = doc(db, 'appointments', selectedInfoAppointment.id);
      await updateDoc(appointmentRef, {
        status: 'assigne'
      });

      fetchAppointments();
      setShowInfoModal(false);
    } catch (error) {
      console.error("Erreur lors de l'approbation du rendez-vous:", error);
    }
  };

  // Fonction pour refuser un rendez-vous en attente
  const handleRejectAppointment = async () => {
    if (!selectedInfoAppointment) return;

    try {
      const appointmentRef = doc(db, 'appointments', selectedInfoAppointment.id);
      await updateDoc(appointmentRef, {
        status: 'libre',
        employeeId: null
      });

      fetchAppointments();
      setShowInfoModal(false);
    } catch (error) {
      console.error("Erreur lors du refus du rendez-vous:", error);
    }
  };

  // Fonction pour annuler sa propre réservation
  const handleCancelReservation = async () => {
    if (!selectedInfoAppointment || selectedInfoAppointment.employeeId !== currentUser.userId) return;

    try {
      const appointmentRef = doc(db, 'appointments', selectedInfoAppointment.id);
      await updateDoc(appointmentRef, {
        status: 'libre',
        employeeId: null
      });

      fetchAppointments();
      setShowInfoModal(false);
    } catch (error) {
      console.error("Erreur lors de l'annulation de la réservation:", error);
    }
  };

  const handleUpdateAppointment = async (updatedAppointment, applyToAll = false) => {
    try {
      if (updatedAppointment.recurrenceId && applyToAll) {
        // Mise à jour de tous les rendez-vous de la série
        const recurrenceQuery = query(
          collection(db, 'appointments'),
          where('recurrenceId', '==', updatedAppointment.recurrenceId)
        );
        const recurrenceSnapshot = await getDocs(recurrenceQuery);

        const batch = writeBatch(db);
        recurrenceSnapshot.docs.forEach(docSnap => {
          const appointmentRef = doc(db, 'appointments', docSnap.id);
          // Mettre à jour tous les champs sauf la date
          const { date, ...updateData } = updatedAppointment;
          batch.update(appointmentRef, updateData);
        });

        await batch.commit();
      } else {
        // Mise à jour d'un seul rendez-vous
        const appointmentRef = doc(db, 'appointments', updatedAppointment.id);
        await updateDoc(appointmentRef, updatedAppointment);
      }

      fetchAppointments();
      setShowEditModal(false);
      setShowRecurrenceModal(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour du rendez-vous:", error);
      alert("Une erreur s'est produite lors de la mise à jour du rendez-vous");
    }
  };

  const handleDeleteAppointment = async (appointmentId, applyToAll = false) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce rendez-vous ?")) {
      return;
    }

    try {
      const appointmentDoc = await getDoc(doc(db, 'appointments', appointmentId));
      const appointmentData = appointmentDoc.data();

      if (appointmentData.recurrenceId && applyToAll) {
        // Suppression de tous les rendez-vous de la série
        const recurrenceQuery = query(
          collection(db, 'appointments'),
          where('recurrenceId', '==', appointmentData.recurrenceId)
        );
        const recurrenceSnapshot = await getDocs(recurrenceQuery);

        const batch = writeBatch(db);
        recurrenceSnapshot.docs.forEach(docSnap => {
          batch.delete(doc(db, 'appointments', docSnap.id));
        });

        await batch.commit();
      } else {
        // Suppression d'un seul rendez-vous
        await deleteDoc(doc(db, 'appointments', appointmentId));
      }

      fetchAppointments();
      setShowEditModal(false);
      setShowRecurrenceModal(false);
    } catch (error) {
      console.error("Erreur lors de la suppression du rendez-vous:", error);
      alert("Une erreur s'est produite lors de la suppression du rendez-vous");
    }
  };

  const renderAppointment = (appointment) => {
    let statusClass, statusText, statusIcon;

    switch (appointment.status) {
      case 'libre':
        statusClass = 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300';
        statusText = 'À combler';
        statusIcon = '⚠️';
        break;
      case 'reserve':
        statusClass = 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
        statusText = 'En attente';
        statusIcon = '⏳';
        break;
      case 'in_progress':
        statusClass = 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
        statusText = 'En cours';
        statusIcon = '▶️';
        break;
      case 'completed':
        statusClass = 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300';
        statusText = 'Terminé';
        statusIcon = '✅';
        break;
      default:
        statusClass = 'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
        statusText = appointment.status || 'Assigné';
        statusIcon = '📅';
    }

    // Déterminer la couleur de la bordure gauche
    const borderColor = appointment.clientColor || (appointment.status === 'libre' ? '#ef4444' : '#6b7280');

    // Style pour l'indicateur de temps
    const timeStyle = "bg-gray-50 dark:bg-white/5 px-2 py-1 rounded text-xs font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1.5";

    return (
      <div
        className={`group relative mb-3 rounded-xl bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-gray-700/50 hover:shadow-md transition-all duration-200 active:scale-[0.98] overflow-hidden ${appointment.status === 'completed' ? 'opacity-75' : ''}`}
        style={{
          borderLeftWidth: '4px',
          borderLeftColor: borderColor
        }}
        onClick={(e) => handleAppointmentClick(appointment, e)}
      >
        <div className="p-3.5">
          {/* En-tête : Client et Statut */}
          <div className="flex justify-between items-start mb-2.5">
            <div className="flex items-center gap-2.5 overflow-hidden pr-2">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-50 dark:bg-gray-700 grid place-items-center text-gray-500 dark:text-gray-400">
                <User size={14} strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate leading-tight">
                  {appointment.clientName || "Bénéficiaire non spécifié"}
                </h4>
                {appointment.clientAddress && typeof appointment.clientAddress === 'string' && (
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {appointment.clientAddress.split(',')[0]}
                  </p>
                )}
              </div>
            </div>

            {statusIcon && appointment.status !== 'assigne' && (
              <span className={`flex-shrink-0 text-[10px] font-semibold px-2 py-1 rounded-full flex items-center gap-1 ${statusClass}`}>
                {statusIcon} <span className="hidden sm:inline">{statusText}</span>
              </span>
            )}
          </div>

          {/* Corps : Horaire et Employé */}
          <div className="flex items-center justify-between">
            <div className={timeStyle}>
              <Clock size={12} className="text-gray-400" />
              <span>{appointment.startTime} - {appointment.endTime}</span>
            </div>

            {appointment.employeeId && (
              <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                <span className="font-medium truncate max-w-[100px]">{getEmployeeName(appointment.employeeId)}</span>
              </div>
            )}
          </div>

          {/* Footer : Notes et Check-in */}
          {((appointment.notes && appointment.notes.content) || (appointment.checkInStatus)) && (
            <div className="mt-3 pt-2.5 border-t border-gray-100 dark:border-gray-700/50 flex items-center gap-3">
              {(appointment.notes && appointment.notes.content) && (
                <div className="flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                  Notes présentes
                </div>
              )}
              {appointment.checkInStatus && appointment.checkInStatus.checkInTime && (
                <div className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 font-medium ml-auto">
                  <Check size={12} strokeWidth={3} />
                  Pointé {new Date(appointment.checkInStatus.checkInTime.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const getEmployeeName = useCallback((employeeId) => {
    const employee = employees.find(emp => emp.userId === employeeId);
    return employee ? (employee.name || `${employee.firstName} ${employee.lastName}`) : 'Employé non trouvé';
  }, [employees]);

  // Fonction pour éclaircir une couleur
  const lightenColor = (color, amount) => {
    return '#' + color.replace(/^#/, '').replace(/../g, color => ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2));
  };

  // Fonction utilitaire pour déterminer si une couleur est claire ou foncée
  const isLightColor = (color) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return brightness > 155;
  };



  return (
    <div className="day-view-mobile flex flex-col h-full overflow-hidden bg-gray-50 dark:bg-gray-900">
      {/* Pas de Header mobile - La date est déjà affichée dans Planning.js */}

      {/* Contenu principal */}
      <div className="flex-1 overflow-auto p-3">
        {/* Section des rendez-vous libres - Style WeekViewMobile */}
        {sortedUnassignedAppointments.length > 0 && (
          <div className="wvm-unassigned-appointments mb-6">
            <h3 className="wvm-unassigned-title flex items-center">
              <AlertCircle className="mr-2 text-yellow-500" size={20} />
              Plages à combler :
            </h3>
            <div className="wvm-unassigned-list flex overflow-x-auto">
              {sortedUnassignedAppointments.map((appointment, index) => (
                <div
                  key={appointment.id}
                  className="mr-3"
                  style={{
                    width: '200px',
                    flexShrink: 0
                  }}
                  onClick={() => handleUnassignedAppointmentClick(appointment)}
                >
                  {renderAppointment(appointment)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Planning de l'utilisateur - Style WeekViewMobile */}
        <div className="wvm-employee-columns grid gap-4">
          {visibleEmployees.every(employee =>
            (!groupedAssignedAppointments[employee.userId] || groupedAssignedAppointments[employee.userId].length === 0)
          ) ? (
            <div className="empty-state flex flex-col items-center justify-center py-12">
              <Calendar className={`mx-auto ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} mb-2`} size={48} />
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Aucun rendez-vous ce jour</p>
            </div>
          ) : (
            visibleEmployees.map(employee => (
              groupedAssignedAppointments[employee.userId] && groupedAssignedAppointments[employee.userId].length > 0 && (
                <div
                  key={employee.userId}
                  className="wvm-employee-column p-4 rounded-lg bg-white dark:bg-gray-800 shadow-sm"
                >
                  {/* Header d'employé mobile */}
                  <div className="wvm-employee-header font-bold text-center mb-4 text-lg flex items-center justify-center">
                    Mon planning
                    {employee.isAdmin && (
                      <Shield className="ml-2 text-blue-500" size={20} title="Droits administratifs" />
                    )}
                  </div>

                  {groupedAssignedAppointments[employee.userId].map((appointment, index) => (
                    <div key={appointment.id} className="mb-3">
                      {renderAppointment(appointment)}
                    </div>
                  ))}
                </div>
              )
            ))
          )}
        </div>
      </div>

      {/* Modales */}
      {showBookingModal && selectedUnassignedAppointment && (
        <EnhancedModal onClose={() => setShowBookingModal(false)} size="md">
          <ModalHeader>Réserver ce rendez-vous</ModalHeader>
          <ModalBody>
            <div className="space-y-3">
              <p className="text-gray-600 dark:text-gray-300">
                Voulez-vous réserver ce rendez-vous ?
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Bénéficiaire :</span>
                  <span className="text-gray-900 dark:text-gray-100">{selectedUnassignedAppointment.clientName || "Bénéficiaire non spécifié"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Date :</span>
                  <span className="text-gray-900 dark:text-gray-100">{selectedUnassignedAppointment.date.toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Heure :</span>
                  <span className="text-gray-900 dark:text-gray-100">{selectedUnassignedAppointment.startTime} - {selectedUnassignedAppointment.endTime}</span>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <ModalButton variant="secondary" onClick={() => setShowBookingModal(false)}>
              Annuler
            </ModalButton>
            <ModalButton variant="primary" onClick={handleBookAppointment}>
              Réserver
            </ModalButton>
          </ModalFooter>
        </EnhancedModal>
      )}

      {showEditModal && selectedEditAppointment && (
        <EnhancedEditAppointmentModal
          appointment={selectedEditAppointment}
          employees={employees}
          clients={clients}
          onClose={() => setShowEditModal(false)}
          onSave={(data) => {
            if (selectedEditAppointment.recurrenceId) {
              setPendingRecurrenceData(data);
              setRecurrenceAction('update');
              setShowRecurrenceModal(true);
            } else {
              handleUpdateAppointment(data);
            }
          }}
          onDelete={(id) => {
            if (selectedEditAppointment.recurrenceId) {
              setPendingRecurrenceData(selectedEditAppointment);
              setRecurrenceAction('delete');
              setShowRecurrenceModal(true);
            } else {
              handleDeleteAppointment(id);
            }
          }}
          canDelete={hasPermission('delete')}
        />
      )}

      {showDetailsModal && selectedDetailAppointment && (
        <EnhancedAppointmentDetailsModal
          appointment={selectedDetailAppointment}
          onClose={() => setShowDetailsModal(false)}
          onUpdate={(updatedAppointment) => {
            // Rafraîchir la liste des rendez-vous après mise à jour (pointage ou clôture)
            fetchAppointments();
            setShowDetailsModal(false);
          }}
          size="full"
          db={db}
        />
      )}

      {/* Modal d'information pour les rendez-vous en attente de validation */}
      {showInfoModal && selectedInfoAppointment && (
        <EnhancedModal onClose={() => setShowInfoModal(false)} size="md">
          <ModalHeader>Détails du rendez-vous</ModalHeader>
          <ModalBody>
            <div className="space-y-3">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Bénéficiaire :</span>
                  <span className="text-gray-900 dark:text-gray-100">{selectedInfoAppointment.clientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Date :</span>
                  <span className="text-gray-900 dark:text-gray-100">{selectedInfoAppointment.date.toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Heure :</span>
                  <span className="text-gray-900 dark:text-gray-100">{selectedInfoAppointment.startTime} - {selectedInfoAppointment.endTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Employé :</span>
                  <span className="text-gray-900 dark:text-gray-100">{getEmployeeName(selectedInfoAppointment.employeeId)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Statut :</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedInfoAppointment.status === 'reserve'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                    }`}>
                    {selectedInfoAppointment.status === 'reserve' ? 'En attente de validation' : selectedInfoAppointment.status}
                  </span>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            {/* Boutons conditionnels selon les permissions */}
            {selectedInfoAppointment.status === 'reserve' && (
              <>
                {/* Boutons pour les utilisateurs avec droits d'écriture */}
                {hasPermission('write') && (
                  <>
                    <ModalButton variant="success" onClick={handleApproveAppointment}>
                      Approuver
                    </ModalButton>
                    <ModalButton variant="danger" onClick={handleRejectAppointment}>
                      Refuser
                    </ModalButton>
                  </>
                )}
                {/* Bouton d'annulation pour l'utilisateur qui a réservé */}
                {selectedInfoAppointment.employeeId === currentUser.userId && (
                  <ModalButton variant="warning" onClick={handleCancelReservation}>
                    Annuler
                  </ModalButton>
                )}
              </>
            )}
            <ModalButton variant="secondary" onClick={() => setShowInfoModal(false)}>
              Fermer
            </ModalButton>
          </ModalFooter>
        </EnhancedModal>
      )}

      {/* Modal de choix pour la récurrence */}
      {showRecurrenceModal && pendingRecurrenceData && (
        <EnhancedModal onClose={() => setShowRecurrenceModal(false)} size="md">
          <ModalHeader>
            {recurrenceAction === 'update' ? 'Modifier le rendez-vous récurrent' : 'Supprimer le rendez-vous récurrent'}
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-300">
                {recurrenceAction === 'update'
                  ? 'Voulez-vous modifier uniquement ce rendez-vous ou tous les rendez-vous de la série ?'
                  : 'Voulez-vous supprimer uniquement ce rendez-vous ou tous les rendez-vous de la série ?'
                }
              </p>
              <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <RotateCcw size={16} className="text-blue-600 dark:text-blue-400" />
                <span className="text-sm text-blue-600 dark:text-blue-400">
                  Ce rendez-vous fait partie d'une série récurrente
                </span>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <ModalButton
              variant="secondary"
              onClick={() => setShowRecurrenceModal(false)}
            >
              Annuler
            </ModalButton>
            <ModalButton
              variant="primary"
              onClick={async () => {
                if (recurrenceAction === 'update') {
                  await handleUpdateAppointment(pendingRecurrenceData, false);
                } else {
                  await handleDeleteAppointment(pendingRecurrenceData.id, false);
                }
              }}
            >
              Ce rendez-vous uniquement
            </ModalButton>
            <ModalButton
              variant="primary"
              onClick={async () => {
                if (recurrenceAction === 'update') {
                  await handleUpdateAppointment(pendingRecurrenceData, true);
                } else {
                  await handleDeleteAppointment(pendingRecurrenceData.id, true);
                }
              }}
            >
              Toute la série
            </ModalButton>
          </ModalFooter>
        </EnhancedModal>
      )}
    </div>
  );
};


export default DayViewMobile;
